import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import ClockIcon from 'icons/Clock.icon';
import { useTranslation } from 'react-i18next';
import useQueryParams from 'hooks/useQueryParams';
import { ReportFilterType } from '../../../../types';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { Button } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import DatepickerField from 'components/DatepickerField';
import validations from 'validations';
import { FIRST_PAGE } from 'data/global';
import { NAVIGATION_VARIANTS, NavigationVariants } from 'models/Navigation';
import { DatepickerRef } from 'ncoded-component-library/build/components/organisms/Datepicker/models';

import './DateFilter.styles.scss';

type DateFilterProps = {
  className?: string;
  variant?: NavigationVariants;
};

const DateFilter: React.FC<DateFilterProps> = (props) => {
  const { className, variant = NAVIGATION_VARIANTS.SECONDARY } = props;
  const { t } = useTranslation();

  const {
    params: { from, to },
    setQueryParam,
    removeQueryParam,
  } = useQueryParams();

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);
  const defaultVal = useMemo(
    () => ({
      from,
      to,
    }),
    [from, to],
  );

  const classes = classNames('date-filter', className);

  const applyQueryFilter = useCallback(
    (key: keyof ReportFilterType, value: string) => {
      if (!value || !value?.length) removeQueryParam(key);
      else setQueryParam(key, value.toString());
    },
    [removeQueryParam, setQueryParam],
  );

  const onFiltersApply = useCallback(
    (values: { from: Date; to: Date }) => {
      const { from, to } = values;
      dropdown.setIsOpen(false);

      if (from) applyQueryFilter('from', new Date(from).toISOString());
      if (to) applyQueryFilter('to', new Date(to).toISOString());
      applyQueryFilter('page', FIRST_PAGE);
    },
    [applyQueryFilter, dropdown],
  );

  const triggerTitle = useMemo(
    () => t(from || to ? 'openToSeeDateFilters' : 'date'),
    [from, t, to],
  );

  const validate = useCallback(
    (values: { from: Date | string; to: Date | string }) => {
      const { from, to } = values;
      if (from && to && new Date(from) > new Date(to))
        return { to: t('endDateBeforeError') };

      return {};
    },
    [t],
  );

  const fromRef = useRef<DatepickerRef>();
  const toRef = useRef<DatepickerRef>();

  useEffect(() => {
    if (!from) {
      fromRef.current?.resetInnerState();
    }

    if (!to) {
      toRef.current?.resetInnerState();
    }
  }, [from, to]);

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      trigerTitle={triggerTitle}
      icon={ClockIcon}
      variant={variant}
    >
      <Form
        onSubmit={onFiltersApply}
        initialValues={defaultVal}
        validate={validate}
        render={({ handleSubmit, pristine }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Field
                name="from"
                label={t('start')}
                onlyPast
                component={DatepickerField}
                validate={validations.composeValidators(
                  validations.onlyPast(t('onlyPast')),
                )}
                ref={fromRef}
              />
              <Field
                name="to"
                label={t('end')}
                onlyPast
                component={DatepickerField}
                validate={validations.composeValidators(
                  validations.onlyPast(t('onlyPast')),
                )}
                setToEndOfDay
                ref={toRef}
              />

              <div className="filter-action-wrapper__buttons">
                <Button
                  variant="link"
                  onClick={() => dropdown?.setIsOpen(false)}
                >
                  {t('cancel')}
                </Button>
                <Button type="submit" variant="outline" disabled={pristine}>
                  {t('applyDateRange')}
                </Button>
              </div>
            </form>
          );
        }}
      />
    </FilterActionWrapper>
  );
};

export default DateFilter;
